import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Box, Typography, styled } from '@mui/material';
import { EMULATOR_TYPES, ExternalLink } from '@localstack/constants';
import { ReactElement, useState } from 'react';
import { useEntitledEmulators } from '@localstack/services';
import { EmulatorToggle, SidebarIcon } from '@localstack/ui';

import { EmulatorType } from '@localstack/types';

import { EMULATORS } from '~/views/customer/PricingPlans/components';

import { BasicPageLayout } from '~/layouts';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { detectOS, InlineLink } from './components/utils';
import { InstallSection, AuthTokenSection, DocTilesGrid } from './components/Sections';
import { UnentitledEmulatorPrompt } from './components/UnentitledEmulatorPrompt';
import { LocalStackLogo } from './components/Icons';

const PageWrapper = styled(Box)(({ theme }) => ({
  maxWidth: 600,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 3),
  },
  [theme.breakpoints.up('md')]: {
    padding: 0,
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  marginTop: 0,
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
}));

const SectionHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'component',
})<{ component?: string }>(({ theme }) => ({
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    rowGap: theme.spacing(0.5),
  },
}));

const INSTALLATION_LINKS: Record<EmulatorType, ExternalLink> = {
  [EmulatorType.AWS]: ExternalLink.DOCS_AWS_GETTING_STARTED,
  [EmulatorType.SNOWFLAKE]: ExternalLink.DOC_SNO_GETTING_STARTED,
  [EmulatorType.AZURE]: ExternalLink.AZURE_GETTING_STARTED,
};

export const GettingStarted = (): ReactElement => {
  const entitledEmulators = useEntitledEmulators({ useAuthProvider });
  const os = detectOS() || 'linux';
  const [selectedEmulator, setSelectedEmulator] = useState<EmulatorType>();

  // Every product we offer, plus any the workspace already holds. The second half covers a
  // product granted before we offer it, so its owner can still reach its docs here.
  const offered: readonly EmulatorType[] = EMULATORS;
  const emulatorsToShowInToggle = EMULATOR_TYPES.filter(
    (candidate) => offered.includes(candidate) || entitledEmulators.includes(candidate),
  );

  const emulator =
    selectedEmulator ?? emulatorsToShowInToggle.find((e) => entitledEmulators.includes(e)) ?? EMULATORS[0];

  const isEntitled = entitledEmulators.includes(emulator);
  const installationLink = INSTALLATION_LINKS[emulator];

  return (
    <BasicPageLayout title="">
      <PageWrapper>
        <HeaderSection>
          <LocalStackLogo />
          <Box>
            <TitleRow>
              <SidebarIcon />
              <Typography variant="h4" fontWeight={600}>
                Getting Started
              </Typography>
            </TitleRow>
            <Typography variant="subtitle1" color="text.tertiary">
              Run LocalStack locally with the CLI or Docker-based workflows.
            </Typography>
          </Box>
        </HeaderSection>

        <SectionHeading component="h2">
          Install CLI{' '}
          <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 400, mx: 0.5 }}>
            &middot;
          </Box>
          <InlineLink
            href={installationLink}
            target="_blank"
            sx={{ fontSize: 14, fontWeight: 500, color: 'text.tertiary' }}
          >
            View installation methods and quickstart <ArrowOutwardIcon sx={{ fontSize: 12, ml: 0.5 }} />
          </InlineLink>
        </SectionHeading>
        <InstallSection os={os} />

        <Box mt={4}>
          <SectionHeading component="h2">
            Personal Auth Token{' '}
            <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 400, mx: 0.5 }}>
              &middot;
            </Box>
            <InlineLink
              href={ExternalLink.DOCS_AUTH_TOKEN}
              target="_blank"
              sx={{ fontSize: 14, fontWeight: 500, color: 'text.tertiary' }}
            >
              Learn how to configure <ArrowOutwardIcon sx={{ fontSize: 12, ml: 0.5 }} />
            </InlineLink>
          </SectionHeading>
          <AuthTokenSection />
        </Box>

        <Box mt={6}>
          <DocTilesGrid
            emulator={emulator}
            emulatorToggleElement={
              <EmulatorToggle
                emulators={emulatorsToShowInToggle}
                value={emulator}
                onChange={setSelectedEmulator}
                size="compact"
              />
            }
            prompt={isEntitled ? undefined : <UnentitledEmulatorPrompt emulator={emulator} />}
          />
        </Box>
      </PageWrapper>
    </BasicPageLayout>
  );
};
